import React, { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import shallow from 'zustand/shallow'

import { datadogRum } from '@datadog/browser-rum'
import { ClientSessionState, VerificationType } from '@frame/lib/api/client-session/types'
import { TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useEmitComplete } from '@frame/lib/hooks/useEmitComplete'
import { usePageState } from '@frame/lib/hooks/usePageLifecycle'
import { useStore } from '@frame/lib/stores/useStore'
import { hexToRgba } from '@frame/lib/utils/colors'
import { router } from '@frame/router'

export function App() {
  // This component is used for any logic that *doesn't* need to be applied the router context. If we need to be running
  // inside of <Router>, we can use the Layout component instead.

  const pageState = usePageState()
  const emitComplete = useEmitComplete()

  const {
    cobrandingColor,
    sessionState,
    sessionId,
    sessionVerifier,
    sessionVerifierType,
    publishableKey,
    sessionEnv,
    enabledStrategies,
    reportType,
    referrer,
  } = useStore(
    (state) => ({
      cobrandingColor: state.session?.configuration?.cobranding?.button_color,
      consumerInitiated: state.session?.consumer_initiated,
      sessionState: state.session?.state ?? ClientSessionState.INITIALIZED,
      sessionId: state.session?.id,
      sessionVerifier: state.session?.verifier,
      sessionVerifierType: state.session?.verifier_type,
      publishableKey: state.config.publishableKey,
      sessionEnv: state.config.env,
      enabledStrategies: state.session?.configuration.enabled_strategies,
      reportType: state.session?.verification_type,
      referrer: state.referrer,
    }),
    shallow
  )

  useEffect(() => {
    const primaryColor = cobrandingColor || '#3E46E7'
    const cobrandingColorHover = hexToRgba({ hex: primaryColor, alpha: 0.1 })
    const cobrandingColorHoverDark = hexToRgba({ hex: primaryColor, darken: 0.2 })
    document.documentElement.style.setProperty('--cobranding-color', primaryColor)
    document.documentElement.style.setProperty('--cobranding-color-hover', cobrandingColorHover)
    document.documentElement.style.setProperty('--cobranding-color-hover-dark', cobrandingColorHoverDark)
  }, [cobrandingColor])

  useEffect(() => {
    datadogRum.setGlobalContextProperty('session', {
      id: sessionId,
      state: sessionState,
      verifier: sessionVerifier,
      verifier_type: sessionVerifierType,
      publishable_key: publishableKey,
      env: sessionEnv,
      enabled_strategies: enabledStrategies,
      referrer,
      is_docs_enabled: enabledStrategies?.includes(TrueworkJsStrategy.DOCUMENTS),
      is_bank_enabled: enabledStrategies?.includes(TrueworkJsStrategy.BANK),
      is_creds_enabled: enabledStrategies?.includes(TrueworkJsStrategy.CREDENTIALS),
      is_tax_enabled: enabledStrategies?.includes(TrueworkJsStrategy.IRS),
      is_tenant_screening_report: reportType === VerificationType.TENANT_SCREENING,
    })
  }, [
    sessionId,
    sessionState,
    sessionVerifier,
    sessionVerifierType,
    publishableKey,
    sessionEnv,
    enabledStrategies,
    reportType,
    referrer,
  ])

  useEffect(() => {
    if (sessionState === ClientSessionState.TERMINAL_COMPLETE) {
      emitComplete()
    }
  }, [emitComplete, sessionState])

  useEffect(() => {
    datadogRum.addAction(`page.state_change.${pageState}`)
    datadogRum.setGlobalContextProperty('page.state', pageState)
  }, [pageState])

  return <RouterProvider router={router} />
}
